import React, {
    useEffect,
    useState
} from "react";

import axios from "axios";

/**
 * Role Permission Management — master-driven.
 *
 * The permission list is rendered from permissions_master/master (the
 * single dev-maintained document). Each role's grants live in
 * partner_role_access/{role_id}; ticking a key writes only that role's
 * document, and unchecked keys simply do not exist in it.
 */
const RolePermissionManagement = () => {

    const API_URL =
        process.env.REACT_APP_API_URL;

    const [roles, setRoles] =
        useState([]);

    const [masterModules, setMasterModules] =
        useState({});

    const [selectedRole, setSelectedRole] =
        useState("");

    const [selectedModule, setSelectedModule] =
        useState("");

    const [
        selectedPermissions,
        setSelectedPermissions
    ] = useState([]);

    // =========================
    // GET ROLES
    // =========================

    const getRoles = async () => {

        try {

            const res =
                await axios.get(
                    `${API_URL}api/admin/roles`
                );

            setRoles(res.data);

        } catch (err) {

            console.log(err);

        }

    };

    // =========================
    // GET MASTER PERMISSION MODULES
    // (permissions_master/master)
    // =========================

    const getMasterModules = async () => {

        try {

            const res = await axios.get(
                `${API_URL}api/admin/permissions/master`
            );

            const master = res.data || {};

            const moduleMap = {};

            Object.keys(master).forEach((key) => {

                if (
                    key === "_master" ||
                    key === "role_id" ||
                    key === "role_name"
                ) return;

                const value = master[key];

                if (
                    value &&
                    typeof value === "object"
                ) {

                    moduleMap[key] = Object.keys(value)
                        .filter((perm) => value[perm] === true);

                } else if (value === true) {

                    // Flat single-key module (e.g. dashboard)
                    moduleMap[key] = [key];

                }

            });

            setMasterModules(moduleMap);

        } catch (err) {

            console.log(err);

        }

    };

    // =========================
    // GET ROLE GRANTS
    // (partner_role_access/{role_id})
    // =========================

    const getRoleAccess = async (roleId) => {

        try {

            const res = await axios.get(
                `${API_URL}api/admin/partner-role-access/${roleId}`
            );

            const data = res.data || {};

            const permissions = [];

            Object.keys(data).forEach((key) => {

                if (
                    key === "role_id" ||
                    key === "role_name" ||
                    key === "createdAt"
                ) return;

                const value = data[key];

                if (
                    value &&
                    typeof value === "object"
                ) {

                    Object.keys(value).forEach(
                        (perm) => {

                            if (value[perm] === true) {

                                permissions.push(perm);

                            }

                        }
                    );

                } else if (value === true) {

                    permissions.push(key);

                }

            });

            setSelectedPermissions(permissions);

        } catch (err) {

            console.log(err);

            setSelectedPermissions([]);

        }

    };

    useEffect(() => {

        getRoles();

        getMasterModules();

    }, []);

    // =========================
    // VISIBLE PERMISSIONS
    // =========================

    const masterPermissions =
        Object.values(masterModules).flat();

    const permissionToModule = {};

    Object.entries(masterModules).forEach(
        ([module, perms]) => {

            perms.forEach((perm) => {

                permissionToModule[perm] = module;

            });

        }
    );

    const visiblePermissionNames =
        masterPermissions.filter(
            (perm) =>
                !selectedModule ||
                permissionToModule[perm] ===
                selectedModule
        );

    const isAllSelected =
        visiblePermissionNames.length > 0 &&
        visiblePermissionNames.every(
            (perm) =>
                selectedPermissions.includes(perm)
        );

    // =========================
    // CHECKBOX
    // =========================

    const handlePermissionChange =
        (permissionName) => {

            if (
                selectedPermissions.includes(
                    permissionName
                )
            ) {

                setSelectedPermissions(

                    selectedPermissions.filter(
                        (item) =>
                            item !==
                            permissionName
                    )

                );

            } else {

                setSelectedPermissions([
                    ...selectedPermissions,
                    permissionName
                ]);

            }

        };

    // =========================
    // SELECT ALL
    // =========================

    const handleSelectAll = () => {

        if (visiblePermissionNames.length === 0) return;

        const allVisibleSelected =
            visiblePermissionNames.every(
                (perm) =>
                    selectedPermissions.includes(perm)
            );

        if (allVisibleSelected) {

            setSelectedPermissions(
                selectedPermissions.filter(
                    (perm) =>
                        !visiblePermissionNames.includes(perm)
                )
            );

        } else {

            const updated = [...selectedPermissions];

            visiblePermissionNames.forEach((perm) => {

                if (!updated.includes(perm)) {

                    updated.push(perm);

                }

            });

            setSelectedPermissions(updated);

        }

    };

    // =========================
    // SAVE (sparse: only true keys)
    // =========================

    const saveRolePermissions = async () => {

        if (!selectedRole) {

            alert("Please Select Role");

            return;

        }

        try {

            const body = {

                role_id: selectedRole,

                role_name: roles.find(
                    (r) =>
                        String(r.role_id) ===
                        String(selectedRole)
                )?.role_name,

            };

            Object.entries(masterModules).forEach(
                ([module, perms]) => {

                    const group = {};

                    perms.forEach((permission) => {

                        if (
                            selectedPermissions.includes(
                                permission
                            )
                        ) {

                            group[permission] = true;

                        }

                    });

                    if (
                        perms.length === 1 &&
                        perms[0] === module
                    ) {

                        // Flat single-key module (dashboard)
                        if (group[module]) {

                            body[module] = true;

                        }

                    } else if (
                        Object.keys(group).length > 0
                    ) {

                        body[module] = group;

                    }

                }
            );

            await axios.post(
                `${API_URL}api/admin/partner-role-access`,
                body
            );

            alert("Permission Saved Successfully");

        } catch (err) {

            console.log(err);

            alert("Save Failed");

        }

    };

    const modules = Object.keys(masterModules);

    return (

    <div
        className="container mt-4"
        style={{
            maxWidth: "1200px",
            margin: "0 auto"
        }}
    >

        <h1
            className="text-center mb-4"
            style={{
                fontWeight: "700",
                color: "#0B2B4C"
            }}
        >
            ROLE PERMISSION MANAGEMENT
        </h1>

        <div
            className="card mb-4"
            style={{
                border: "none",
                borderRadius: "15px",
                boxShadow:
                    "0 4px 15px rgba(0,0,0,0.08)",
                overflow: "hidden"
            }}
        >

            <div
                style={{
                    background: "#0B2B4C",
                    color: "#fff",
                    padding: "15px 25px",
                    fontSize: "22px",
                    fontWeight: "600"
                }}
            >
                🔐 Role Permission Management
            </div>

            <div className="p-4">

                <div className="row align-items-end">

                   {/* Role */}
<div className="col-md-4">

    <label
        className="mb-2"
        style={{ fontWeight: "600" }}
    >
        Role
    </label>

    <select
        className="form-select"
        value={selectedRole}
        onChange={(e) => {

            const roleId = e.target.value;

            setSelectedRole(roleId);

            getRoleAccess(roleId);

        }}
    >

        <option value="">
            Select Role
        </option>

        {roles.map((role) => (

            <option
                key={role.role_id}
                value={role.role_id}
            >
                {role.role_name}
            </option>

        ))}

    </select>

</div>

<div className="col-md-4">

    <label
        className="mb-2"
        style={{ fontWeight: "600" }}
    >
        Module
    </label>

    <select
        className="form-select"
        value={selectedModule}
        onChange={(e) =>
            setSelectedModule(
                e.target.value
            )
        }
    >

        <option value="">
            Select Module
        </option>

        {modules.map(
            (module, index) => (

                <option
                    key={`${module}-${index}`}
                    value={module}
                >
                    {module}
                </option>

            )
        )}

    </select>

</div>

                    {/* Save */}
                    <div className="col-md-4">

                        <button
                            className="btn btn-success w-100"
                            onClick={
                                saveRolePermissions
                            }
                        >
                            Save
                        </button>

                    </div>

                </div>

            </div>

        </div>

  <div className="mt-4">

    <table className="table table-bordered table-striped">

        <thead>
            <tr>
                <th>Module</th>
                <th>Permission Name</th>
                <th style={{ width: "150px", textAlign: "center" }}>
                    <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        style={{ marginRight: "5px", cursor: "pointer" }}
                    />
                    Access
                </th>
            </tr>
        </thead>

       <tbody>

{
    visiblePermissionNames
        .map((permission) => (

        <tr key={permission}>

            <td>{permissionToModule[permission]}</td>

            <td>{permission}</td>

            <td className="text-center">

                <input
                    type="checkbox"
                    checked={selectedPermissions.includes(permission)}
                    onChange={() =>
                        handlePermissionChange(permission)
                    }
                />

            </td>

        </tr>

    ))
}

</tbody>
    </table>

</div>

                </div>

            

        );
    
};

export default RolePermissionManagement;
